import React, { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Ban, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const ago = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

export default function BanItem({ ban, onChanged }) {
  const [busy, setBusy] = useState(false);

  const unban = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bans/${ban.user_id}/unban`, { reason: null });
      toast.success(`@${ban.username} can use Yaprio again`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:shadow-md sm:flex-row sm:items-center sm:p-5"
      data-testid={`ban-item-${ban.user_id}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-50">
        <Ban className="h-5 w-5 text-red-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-heading text-sm font-bold text-slate-900">@{ban.username}</p>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${
              ban.active ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"
            }`}
          >
            {ban.active ? (ban.expires_at ? ban.duration : "permanent") : ban.ended_reason || "lifted"}
          </span>
        </div>
        <p className="mt-1 break-words text-sm text-slate-600">{ban.reason}</p>
        <p className="mt-1 text-xs text-slate-400">
          by @{ban.banned_by_username} · {ago(ban.created_at)}
          {ban.expires_at ? ` · expires ${new Date(ban.expires_at).toLocaleString()}` : ""}
        </p>
      </div>
      {ban.active && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={unban}
          data-testid={`btn-unban-${ban.user_id}`}
          className="shrink-0 rounded-full border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          <Undo2 className="mr-1.5 h-4 w-4" />
          Unban
        </Button>
      )}
    </div>
  );
}
